import { useState, useEffect } from "react";
import { Menu, X, ChevronRight } from "lucide-react";
import { Link, useLocation } from "react-router";
import { motion, AnimatePresence } from "motion/react";
import { ProjectModal } from "./ProjectModal";
import { Logo } from "./Logo";

export function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { name: "Home", to: "/" },
    { name: "Services", to: "/services" },
    { name: "Articles", to: "/articles" },
    { name: "Industries", to: "/industries" },
    { name: "About", to: "/about" },
    { name: "Contact", to: "/contact" },
  ];

  const isLinkActive = (path: string) => {
    if (path === "/") {
      return location.pathname === "/";
    }
    return location.pathname.startsWith(path);
  };

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? "bg-white/90 backdrop-blur-md shadow-sm py-3 md:py-4" : "bg-transparent py-4 md:py-6"
        }`}
      >
        <div className="container mx-auto px-6 flex items-center justify-between">
          {/* Logo Placeholder */}
          <Link to="/" className="flex items-center gap-2 group text-slate-900">
            <Logo className="h-10 md:h-14 w-auto text-slate-900 group-hover:scale-105 transition-transform" />
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.name}
                to={link.to}
                className={`text-sm font-medium transition-colors ${
                  isLinkActive(link.to)
                    ? "text-[#FD5750]"
                    : "text-slate-600 hover:text-[#FD5750]"
                }`}
              >
                {link.name}
              </Link>
            ))}
            <button
              onClick={() => setIsModalOpen(true)}
              className="px-5 py-2.5 text-sm font-semibold text-white bg-[#FD5750] hover:bg-[#E04F49] rounded-full transition-colors shadow-lg shadow-[#FD5750]/20 flex items-center gap-2 cursor-pointer"
            >
              Book a Call <ChevronRight size={16} />
            </button>
          </div>

          {/* Mobile Toggle */}
          <button
            className="md:hidden text-slate-800 p-2"
            onClick={() => setIsOpen(!isOpen)}
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden bg-white border-b border-slate-100 overflow-hidden"
            >
              <div className="px-6 py-6 flex flex-col gap-4">
                {navLinks.map((link) => (
                  <Link
                    key={link.name}
                    to={link.to}
                    className={`text-lg font-medium ${
                      isLinkActive(link.to)
                        ? "text-[#FD5750]"
                        : "text-slate-800 hover:text-[#FD5750]"
                    }`}
                    onClick={() => setIsOpen(false)}
                  >
                    {link.name}
                  </Link>
                ))}
                <button
                  onClick={() => {
                    setIsOpen(false);
                    setIsModalOpen(true);
                  }}
                  className="mt-2 w-full text-center px-5 py-3 text-base font-semibold text-white bg-[#FD5750] rounded-lg cursor-pointer"
                >
                  Book a Free Call
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>

      <ProjectModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </>
  );
}
